'use client';

import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

interface TeamMember {
  name: string;
  title: string;
  role: string;
  expertise: string[];
  certifications: string[];
  specializations: string[];
  linkedIn?: string;
}

const TeamMembersSection = () => {
  const teamMembers: TeamMember[] = [
    {
      name: 'Dr. Nupur',
      title: 'Director',
      role: 'ESG Board Committee Member',
      expertise: [
        'Global Certified ESG Expert',
        'Rapid BRSR Report Provider using Tech/AI',
        'GRI, GHG, CSRD SAP EHS Data Expert',
        'Carbon Accounting & AI for ESG Global Expert',
        'International Carbon Credit Expert',
        'Solar Certified Professional',
      ],
      certifications: [
        'Green Finance Deposit Banker',
        'Carbon Sequestering Solutions Specialist',
      ],
      specializations: [
        'Forestry - Dharohar MIHSTI Program Advocate',
        'Technology-Driven ESG Reporting',
        'AI-Powered Sustainability Solutions',
      ],
      linkedIn: 'https://www.linkedin.com/in/nupurmy/',
    },
    {
      name: 'Mrs. Padmaja Reddy',
      title: 'Director',
      role: 'GIS & Remote Sensing Expert',
      expertise: [
        'Remote Sensing & GIS Photogrammetry',
        'Drone Mapping Technology',
        'Custom GIS Software Development',
        'Defense & Paramilitary GIS Solutions',
      ],
      certifications: [
        'NRSA Certified Professional',
        'Survey of India Specialist',
      ],
      specializations: [
        'Sustainable Textile Innovation',
        'Textile Marketing & Export',
        'Geospatial Intelligence Systems',
      ],
    },
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-text-primary mb-4">
            Leadership Profiles
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Our directors bring unparalleled expertise in ESG compliance, carbon accounting, 
            AI technology, and geospatial intelligence to drive sustainable transformation.
          </p>
        </div>

        {/* Team Members Grid */}
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {teamMembers.map((member, index) => (
            <div
              key={index}
              className="bg-card border border-border rounded-2xl p-8 hover:shadow-natural-xl transition-all duration-500 ease-organic group"
            >
              {/* Header */}
              <div className="mb-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-2xl font-heading font-bold text-text-primary mb-1 group-hover:text-primary transition-colors">
                      {member.name}
                    </h3>
                    <p className="text-primary font-semibold mb-1">{member.title}</p>
                    <p className="text-sm text-text-secondary">{member.role}</p>
                  </div>
                  {member.linkedIn && (
                    <Link
                      href={member.linkedIn}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 bg-primary/10 hover:bg-primary text-primary hover:text-white rounded-lg transition-all duration-300"
                    >
                      <Icon name="GlobeAltIcon" size={20} />
                    </Link>
                  )}
                </div>
              </div>

              {/* Expertise */}
              <div className="mb-6">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="AcademicCapIcon" size={20} className="text-primary" />
                  <h4 className="font-semibold text-text-primary">Core Expertise</h4>
                </div>
                <ul className="space-y-2">
                  {member.expertise.map((item, idx) => (
                    <li key={idx} className="flex items-start space-x-2 text-sm text-text-secondary">
                      <Icon name="CheckCircleIcon" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Certifications */}
              <div className="mb-6">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="ShieldCheckIcon" size={20} className="text-secondary" />
                  <h4 className="font-semibold text-text-primary">Certifications</h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {member.certifications.map((cert, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-secondary/10 text-secondary text-xs font-medium rounded-full"
                    >
                      {cert}
                    </span>
                  ))}
                </div>
              </div>

              {/* Specializations */}
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="SparklesIcon" size={20} className="text-accent" />
                  <h4 className="font-semibold text-text-primary">Specializations</h4>
                </div>
                <div className="space-y-2">
                  {member.specializations.map((spec, idx) => (
                    <div
                      key={idx}
                      className="flex items-center space-x-2 text-sm text-text-secondary"
                    >
                      <div className="w-1.5 h-1.5 bg-accent rounded-full"></div>
                      <span>{spec}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TeamMembersSection;